// Portal domain types — mirrors the data-model sketch in
// docs/revamp/03_BACKEND_AND_PROFESSIONAL_LAYER.md §2, as locked by
// docs/revamp/05_PORTAL_DESIGN.md (2026-06-17).
//
// Non-negotiables encoded here:
//   • A child is a pseudonymous UUID. The ONLY child-identifying field is an
//     owner-set `displayLabel` (rendered client-side). No name/age/PII and no
//     clinical/therapy/diagnostic fields ever live on `Child`.
//   • Portal users are a single `account` type — there is no parent/professional
//     role split. The relationship is per child via `ChildLink.relation`.
//   • Shared data is aggregated engagement summaries only (never scores/outcomes,
//     never clinical assessment).

/** ISO-8601 timestamp string, e.g. "2026-03-12T09:30:00.000Z". */
export type IsoTimestamp = string;

/** Opaque identifier. Children use a pseudonymous UUID. */
export type Id = string;

// --- Organisation + accounts -------------------------------------------------

/** Kind of organisation buying seats. Informational, never an access gate. */
export type OrganisationType = "centre" | "clinic" | "school" | "other";

export interface Organisation {
  id: Id;
  name: string;
  type: OrganisationType;
  /** Total seats the licence permits. */
  seatLimit: number;
  createdAt: IsoTimestamp;
}

/**
 * An adult account. Magic-link auth, no passwords. `orgId` is informational /
 * billing only — it is NOT a parent/professional gate (see 03_ §2).
 */
export interface Account {
  id: Id;
  email: string;
  orgId?: Id;
  createdAt: IsoTimestamp;
}

/** Org membership. `member` = seat-holder; `admin` manages the org. */
export type MembershipRole = "admin" | "member";

export interface Membership {
  accountId: Id;
  orgId: Id;
  role: MembershipRole;
}

// --- Child + per-child relationship ------------------------------------------

/**
 * A child as the backend knows them: a pseudonymous UUID owned by an adult,
 * plus an owner-set display label. This shape is intentionally exhaustive —
 * NO further fields may be added (no name, age, guide, domain, scores, or any
 * clinical/diagnostic data). PII stays on-device only.
 */
export interface Child {
  id: Id;
  ownerAccountId: Id;
  displayLabel: string;
}

/** The exact, exhaustive set of keys a `Child` may carry. */
export const CHILD_ALLOWED_KEYS = [
  "id",
  "ownerAccountId",
  "displayLabel",
] as const satisfies ReadonlyArray<keyof Child>;

/** Granular, revocable consent scopes shared with a viewer. */
export type ConsentScopeKey = "progress" | "results" | "export";

/** Per-child relationship. Owner controls consent; viewer sees consented scope. */
export type ChildRelation = "owner" | "viewer";

export interface ChildLink {
  childId: Id;
  accountId: Id;
  relation: ChildRelation;
  /** Links to the governing consent record (owner has implicit full scope). */
  consentId?: Id;
  /** Scopes this link may see. Owners hold all; viewers hold the consented set. */
  scope: ConsentScopeKey[];
  /** How this adult supports the child (viewer links; migration 20261022000008). */
  role?: LinkRole | null;
  /** A short adult-facing label for this link ("Ms Aisyah, speech"). Never about the child. */
  label?: string | null;
  createdAt?: IsoTimestamp;
}

/** The role an adult holds on one child's link. */
export type LinkRole = "family" | "therapist" | "teacher" | "caregiver" | "other";

export const LINK_ROLES: readonly LinkRole[] = ["family", "therapist", "teacher", "caregiver", "other"];

/** Granular + revocable consent grant. `revokedAt` set => access cut + purged. */
export interface Consent {
  id: Id;
  scope: ConsentScopeKey[];
  grantedBy: Id;
  grantedAt: IsoTimestamp;
  revokedAt?: IsoTimestamp;
}

// --- Engagement summaries (aggregated, pseudonymous, never scores) -----------

/**
 * Aggregated engagement summary for one game session. Engagement only — these
 * are not test scores or clinical outcomes. `accuracy` is a coarse rounds-completed
 * signal the portal never displays; it is null for the many games that do not
 * measure it (finding F-11: "not measured" is not zero).
 */
export interface SessionSummary {
  childId: Id;
  gameId: string;
  /** Session length in seconds. */
  durationSeconds: number;
  /** Coarse engagement signal in [0, 1], or null when the game does not measure it. Never shown. */
  accuracy: number | null;
  at: IsoTimestamp;
}

/** Rolling per-domain engagement level. Plain-language, not an assessment. */
export interface SkillSummary {
  childId: Id;
  /** Plain-language play domain, e.g. "Listening & letters". */
  domain: string;
  /** Coarse engagement level in [0, 1]. Not a clinical level. */
  level: number;
  updatedAt: IsoTimestamp;
}

// --- Assignments -------------------------------------------------------------

export type AssignmentStatus = "assigned" | "in-progress" | "explored";

export interface Assignment {
  id: Id;
  childId: Id;
  assignedByAccountId: Id;
  /** A game id or a plain-language skill/domain target. */
  target: string;
  status: AssignmentStatus;
  at: IsoTimestamp;
  /** The centre's short note to the family (<= 280 chars). Shown in the app as "From your centre". */
  note?: string | null;
  /** When the family's app first showed the suggestion. Null/absent = not opened yet. */
  seenAt?: IsoTimestamp | null;
  updatedAt?: IsoTimestamp;
}

// --- Skill steps (progress views phase 2) ------------------------------------
//
// A FIXED-SIZE snapshot synced from the family's app: one row per child x skill
// x step, dates rounded on the device to the Monday of their week. No text about
// the child, no age. See migration 20261022000007 and the progress-views design.

/** The stage ladder, lowest first. "Not started" is the absence of a row. */
export type StepStage = "exploring" | "help" | "own" | "steady" | "places";

/** ISO calendar date "YYYY-MM-DD" (week-rounded to Monday on the device). */
export type IsoDate = string;

export interface SkillStepState {
  childId: Id;
  skillId: string;
  /** 1..4 */
  step: number;
  stage: StepStage;
  exploreOn: IsoDate | null;
  helpOn: IsoDate | null;
  ownOn: IsoDate | null;
  steadyOn: IsoDate | null;
  placesOn: IsoDate | null;
  lastPractisedOn: IsoDate | null;
  updatedAt: IsoTimestamp;
}

/** "How best to support me" — the app's accessibility settings. Null = not shared. */
export interface SupportProfile {
  childId: Id;
  captions: boolean | null;
  reducedMotion: boolean | null;
  highContrast: boolean | null;
  calmMode: boolean | null;
  tapOnly: boolean | null;
  language: string | null;
  updatedAt: IsoTimestamp;
}

// --- Licence + seats ---------------------------------------------------------

/** Plan is negotiated with us — no online checkout (read-only in the UI). */
export interface License {
  orgId: Id;
  seatsUsed: number;
  seatsTotal: number;
  plan: string;
  /** Human-readable billing period, e.g. "annual". */
  period: string;
}

export type SeatStatus = "active" | "pending";

export interface Seat {
  orgId: Id;
  /** Account holding (or invited to hold) the seat. */
  accountId: Id;
  status: SeatStatus;
}

// --- Member invitations ------------------------------------------------------

/**
 * A pending invitation for an adult to join an org as admin/member, recorded by
 * the `invite_member` RPC. The email is an ADULT account email — never child
 * PII. Sending the actual email is a later Edge Function follow-up; this row is
 * just the data record an admin can see and cancel.
 */
export interface MemberInvitation {
  id: Id;
  orgId: Id;
  email: string;
  role: MembershipRole;
  invitedBy: Id;
  expiresAt: IsoTimestamp;
  createdAt: IsoTimestamp;
}

/** A parent's "Seen at home" note, shared with chosen adults (table home_note). */
export interface HomeNote {
  id: Id;
  childId: Id;
  skillId: string;
  body: string;
  /** Monday of the week the note is about, "YYYY-MM-DD". */
  weekOf: string;
  createdAt: string;
}
